import React, { useCallback, useEffect, useState } from 'react';
import { postReferral, getSubSpecialities } from '@actions';
import { useDispatch } from 'react-redux';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSessionFormState } from '@hooks/useSessionFormState';
import { useSessionAction } from '@hooks/useSessionAction';
import { Alert, Form } from 'antd';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { useDebounceCallback } from 'usehooks-ts';
import type { SessionForm } from '@components/Clinicians/session-form';
import { useSessionOutcome } from '@hooks/useSessionOutcome';
import { toPairs } from 'lodash';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import useAppointmentDetails from '@hooks/appointments/useAppointmentDetails';
import { useDPACheck } from '@hooks/useDPACheck';
import { useSessionAppointmentId } from '@hooks/useSessionAppointmentId';
import useApptReferral from '@components/Clinicians/Session/hooks/useApptReferral';
import { alertModal } from 'vl-common/src/components/Modals';
import { SelectClinicianFormItem } from './SelectClinicianFormItem';
import { FaceToFaceUrgencyFormItem } from './FaceToFaceUrgencyFormItem';
import { OTHER_OPTION, ReasonForReferralFormItem } from './ReasonForReferralFormItem';
import { SpecialityFormItem } from './SpecialityFormItem';
import { SubspecialityFormItem } from './SubspecialityFormItem';
import AdditionalInformationFormItem from './AdditionalInformationFormItem';
import DetailsOfClinicFormItem from './DetailsOfClinicFormItem';
import {
  ACTION_REFF2F_CODE,
  ACTION_AUTH_KEY,
  ACTION_AUTH_START_KEY,
  ACTION_SPECIALITY_KEY,
  ACTION_SUBSPECIALITY_KEY,
  ACTION_REASON_KEY,
  ACTION_OTHER_REASON_KEY,
  ACTION_REFCLI_KEY,
  ACTION_URGENCY_KEY
} from '../../../session-form';
import { AuthSelfBookFormItem } from './AuthSelfBookFormItem';
import { AuthStartDateFormItem } from './AuthStartDateFormItem';
import ActionSubtypeFormItem from './ActionSubtypeFormItem';

const isDateBeforeTomorrow = (date: Date | undefined) => {
  if (!date) {
    return false;
  }
  return dayjs(date).isBefore(dayjs().startOf('day'));
};

type InputRequirement = 'required' | 'optional' | 'disabled';

const REFF2F_AUTH_KEY = `${ACTION_AUTH_KEY}_${ACTION_REFF2F_CODE}`;
const REFF2F_AUTH_START_KEY = `${ACTION_AUTH_START_KEY}_${ACTION_REFF2F_CODE}`;
const REFF2F_REFCLI_KEY = `${ACTION_REFCLI_KEY}_${ACTION_REFF2F_CODE}`;
const REFF2F_SPECIALITY_KEY = `${ACTION_SPECIALITY_KEY}_${ACTION_REFF2F_CODE}`;
const REFF2F_SUBSPECIALITY_KEY = `${ACTION_SUBSPECIALITY_KEY}_${ACTION_REFF2F_CODE}`;
const REFF2F_REASON_KEY = `${ACTION_REASON_KEY}_${ACTION_REFF2F_CODE}`;
const REFF2F_OTHER_REASON_KEY = `${ACTION_OTHER_REASON_KEY}_${ACTION_REFF2F_CODE}`;
const REFF2F_URGENCY_KEY = `${ACTION_URGENCY_KEY}_${ACTION_REFF2F_CODE}`;

type FaceToFaceFieldsetSessionFormKeys = Extract<
  keyof SessionForm,
  | typeof REFF2F_AUTH_KEY
  | typeof REFF2F_AUTH_START_KEY
  | typeof REFF2F_REFCLI_KEY
  | typeof REFF2F_SPECIALITY_KEY
  | typeof REFF2F_SUBSPECIALITY_KEY
  | typeof REFF2F_REASON_KEY
  | typeof REFF2F_OTHER_REASON_KEY
  | typeof REFF2F_URGENCY_KEY
>;

export const useFaceToFaceFieldsetRequirements = (): Record<FaceToFaceFieldsetSessionFormKeys, InputRequirement> => {
  const dispatch = useDispatch();

  const {
    meta,
    selectedSubType,
    AUTH_KEY,
    AUTH_START_KEY,
    SPECIALITY_KEY,
    REASON_KEY,
    REFCLI_KEY,
    SUBSPECIALITY_KEY,
    URGENCY_KEY,
    OTHER_REASON_KEY
  } = useSessionAction(ACTION_REFF2F_CODE);

  const [speciality] = useSessionFormState(SPECIALITY_KEY);
  const [reasonCode] = useSessionFormState(REASON_KEY);

  const actionSubtypes = meta?.action_subtypes?.find(
    ({ action_subtype_code }) => action_subtype_code === selectedSubType
  );

  const showAuthCheckbox = actionSubtypes?.auth_enabled;

  const { data: requiresSubspeciality } = useQuery({
    queryKey: [`subSpecialitiesMeta${ACTION_REFF2F_CODE}`, speciality],
    queryFn: () => dispatch(getSubSpecialities(String(speciality), 1)),
    select: (data) => {
      if (!speciality) return false;
      const subSpecs = data?.areas ?? (data as any)?.[speciality];
      return Number((subSpecs || []).length) > 0;
    },
    enabled: !!speciality,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  const requiresReferral = Number(actionSubtypes?.action_spec_cat_codes?.length) > 0;
  const isConsultantReferral = actionSubtypes?.action_subtype_code === 'CONSULT';

  return {
    [AUTH_KEY]: showAuthCheckbox ? 'optional' : 'disabled',
    [AUTH_START_KEY]: showAuthCheckbox ? 'optional' : 'disabled',
    [REFCLI_KEY]: actionSubtypes?.action_subtype_code === 'CONSULT' ? 'optional' : 'disabled',
    [SPECIALITY_KEY]: requiresReferral && isConsultantReferral ? 'required' : 'disabled',
    [SUBSPECIALITY_KEY]: requiresSubspeciality && isConsultantReferral ? 'required' : 'disabled',
    [URGENCY_KEY]: Number(actionSubtypes?.action_urgency_codes?.length) > 1 ? 'required' : 'disabled',
    [REASON_KEY]: requiresReferral ? 'required' : 'disabled',
    [OTHER_REASON_KEY]: requiresReferral && reasonCode === OTHER_OPTION.value ? 'required' : 'disabled'
  };
};

export const useValidateFaceToFaceFieldset = () => {
  const form = Form.useFormInstance<SessionForm>();
  const {
    SUBTYPE_KEY,
    AUTH_KEY,
    AUTH_START_KEY,
    SPECIALITY_KEY,
    SUBSPECIALITY_KEY,
    REASON_KEY,
    OTHER_REASON_KEY,
    URGENCY_KEY,
    REFCLI_KEY
  } = useSessionAction(ACTION_REFF2F_CODE);

  const [selectedSubType] = useSessionFormState(SUBTYPE_KEY);
  const [auth] = useSessionFormState(AUTH_KEY);
  const [authStart] = useSessionFormState(AUTH_START_KEY);
  const [speciality] = useSessionFormState(SPECIALITY_KEY);
  const [subspeciality] = useSessionFormState(SUBSPECIALITY_KEY);
  const [referral_reason_code] = useSessionFormState(REASON_KEY);
  const [ReasonForReferral] = useSessionFormState(OTHER_REASON_KEY);
  const [urgency_code] = useSessionFormState(URGENCY_KEY);
  const [refcli] = useSessionFormState(REFCLI_KEY);

  const DISPLAY_NAMES: Record<FaceToFaceFieldsetSessionFormKeys, string> = {
    [AUTH_KEY]: 'Allow patient to self book?',
    [AUTH_START_KEY]: 'Self-booking start date',
    [REFCLI_KEY]: 'Suggested clinician',
    [SPECIALITY_KEY]: 'Referral specialty',
    [SUBSPECIALITY_KEY]: 'Referral subspecialty',
    [REASON_KEY]: 'Reason for referral',
    [OTHER_REASON_KEY]: 'Other reason for referral',
    [URGENCY_KEY]: 'Referral urgency'
  };

  const sessionFormStateSource: {
    [K in FaceToFaceFieldsetSessionFormKeys]: SessionForm[K];
  } = {
    [AUTH_KEY]: auth,
    [AUTH_START_KEY]: authStart,
    [REFCLI_KEY]: refcli,
    [SPECIALITY_KEY]: speciality,
    [SUBSPECIALITY_KEY]: subspeciality,
    [REASON_KEY]: referral_reason_code,
    [OTHER_REASON_KEY]: ReasonForReferral,
    [URGENCY_KEY]: urgency_code
  };

  const { selectedActions, appointmentVersion } = useSessionOutcome();
  const inputRequirements = useFaceToFaceFieldsetRequirements();

  const subTypeErrorMessage = 'Specify to whom you are referring';

  return useCallback(() => {
    if (appointmentVersion !== 2) return [];
    if (!selectedActions.map(({ appt_action_code }) => appt_action_code).includes(ACTION_REFF2F_CODE)) return [];

    const errors: string[] = [];

    if (!selectedSubType) {
      form.setFields([{ name: SUBTYPE_KEY, errors: [subTypeErrorMessage] }]);
      errors.push(subTypeErrorMessage);
    } else {
      form.setFields([{ name: SUBTYPE_KEY, errors: [] }]);
    }

    const requiredFields = toPairs(inputRequirements).filter(([, value]) => value === 'required') as [
      FaceToFaceFieldsetSessionFormKeys,
      InputRequirement
    ][];

    for (const [key] of requiredFields) {
      const sessionStoredValue = sessionFormStateSource[key];
      if (!sessionStoredValue) {
        const errorMessage = `${DISPLAY_NAMES[key]} is required`;

        form.setFields([{ name: key, errors: [errorMessage] }]);
        errors.push(errorMessage);
      } else {
        form.setFields([{ name: key, errors: [] }]);
      }
    }

    return errors;
  }, [appointmentVersion, form, inputRequirements, selectedActions, selectedSubType, SUBTYPE_KEY]);
};

const useFaceToFaceReferralMutation = () => {
  const dispatch = useDispatch();
  const { SPECIALITY_KEY, SUBSPECIALITY_KEY, REASON_KEY, POSTCODE_KEY, OTHER_REASON_KEY, URGENCY_KEY, REFCLI_KEY } =
    useSessionAction(ACTION_REFF2F_CODE);
  const { appointment } = useSessionAppointment();
  const appointmentId = useSessionAppointmentId(true);
  const { selectedSubType } = useSessionAction(ACTION_REFF2F_CODE);

  const [speciality] = useSessionFormState(SPECIALITY_KEY);
  const [subspeciality] = useSessionFormState(SUBSPECIALITY_KEY);
  const [ref_reason_code] = useSessionFormState(REASON_KEY);
  const [otherReason] = useSessionFormState(OTHER_REASON_KEY);
  const [postcode] = useSessionFormState(POSTCODE_KEY);
  const [urg_code] = useSessionFormState(URGENCY_KEY);
  const [referredClinician] = useSessionFormState(REFCLI_KEY);

  const inputRequirements = useFaceToFaceFieldsetRequirements();

  return useMutation({
    mutationFn: () => {
      if (
        !speciality ||
        (inputRequirements[SUBSPECIALITY_KEY] === 'required' && !subspeciality) ||
        appointment?.appointment_completed ||
        appointment?.read_only ||
        selectedSubType === 'OSERV'
      ) {
        return Promise.reject();
      }

      const body = {
        appt_id: appointmentId,
        hospital_id: referredClinician?.hospital_id ?? undefined,
        postcode: postcode ?? appointment?.patient_postcode,
        specarea: subspeciality ?? appointment.case_spec_area_code ?? undefined,
        speccat: speciality,
        urg_code: inputRequirements[URGENCY_KEY] === 'required' ? urg_code : 'ROU',
        user_guid: appointment.clinician_user_guid,
        refclin_guid: referredClinician?.clin_user_guid ?? undefined
      } satisfies Partial<Parameters<typeof postReferral>[0]>;

      const isOtherReasonForReferralSelected = ref_reason_code === OTHER_OPTION.value;

      if (otherReason && isOtherReasonForReferralSelected)
        return dispatch(postReferral({ ...body, reason: otherReason }));
      if (ref_reason_code?.trim() && !isOtherReasonForReferralSelected)
        return dispatch(postReferral({ ...body, ref_reason_code }));

      return Promise.reject();
    }
  });
};

const useInitialiseFaceToFaceFieldset = () => {
  const dispatch = useDispatch();
  const { appointment } = useSessionAppointment();
  const appointmentId = useSessionAppointmentId();

  const {
    selectedSubType,
    selectedSubTypeMeta,
    SPECIALITY_KEY,
    SUBSPECIALITY_KEY,
    REASON_KEY,
    POSTCODE_KEY,
    OTHER_REASON_KEY,
    URGENCY_KEY,
    REFCLI_KEY,
    COMMENT_KEY
  } = useSessionAction(ACTION_REFF2F_CODE);

  const { referral, referralStatus } = useApptReferral();

  const { detailsCurrentAppointment } = useAppointmentDetails(appointmentId);

  const comment = detailsCurrentAppointment?.appt_actions?.find(
    ({ appt_action_code }) => appt_action_code === ACTION_REFF2F_CODE
  )?.action_task_comment;

  const [speciality, setSpeciality] = useSessionFormState(SPECIALITY_KEY);
  const [subSpeciality, setSubspeciality] = useSessionFormState(SUBSPECIALITY_KEY);
  const [, setReasonCode] = useSessionFormState(REASON_KEY);
  const [, setOtherReason] = useSessionFormState(OTHER_REASON_KEY);
  const [, setPostcode] = useSessionFormState(POSTCODE_KEY);
  const [, setUrgencyCode] = useSessionFormState(URGENCY_KEY);
  const [, setReferredClinician] = useSessionFormState(REFCLI_KEY);
  const [, setActionTaskComment] = useSessionFormState(COMMENT_KEY);

  const {
    data: { requiresSubspeciality, subSpecialitiesMeta } = { requiresSubspeciality: false, subSpecialitiesMeta: null }
  } = useQuery({
    queryKey: [`subSpecialitiesMeta${ACTION_REFF2F_CODE}`, speciality],
    queryFn: () => dispatch(getSubSpecialities(String(speciality), 1)),
    select: (data) => {
      if (!speciality) return { requiresSubspeciality: false, subSpecialitiesMeta: null };
      const subSpecs = data?.areas ?? (data as any)?.[speciality]; // NOSONAR
      return { requiresSubspeciality: Number((subSpecs || []).length) > 0, subSpecialitiesMeta: subSpecs };
    },
    enabled: !!speciality,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  useEffect(() => {
    if (subSpeciality !== undefined || referralStatus !== 'success' || !requiresSubspeciality) return;

    const defaultSubSpeciality = referral?.ref_case_spec_area_code || appointment.case_spec_area_code;

    if (
      defaultSubSpeciality &&
      subSpecialitiesMeta.filter((subSpec: any) => subSpec.area_code === defaultSubSpeciality)?.length > 0
    ) {
      setSubspeciality(defaultSubSpeciality);
    }
  }, [
    subSpeciality,
    referral,
    referralStatus,
    appointment.case_spec_area_code,
    setSubspeciality,
    requiresSubspeciality,
    subSpecialitiesMeta
  ]);

  useEffect(() => {
    if (speciality !== undefined || referralStatus !== 'success') return;

    const defaultSpeciality = referral?.ref_case_spec_cat_code || appointment.spec_cat_code;
    if (defaultSpeciality) setSpeciality(defaultSpeciality);
  }, [speciality, referral, referralStatus, appointment.spec_cat_code, setSpeciality]);

  useEffect(() => {
    if (referral?.urgency_code) setUrgencyCode(referral.urgency_code);
  }, [referral?.urgency_code, setUrgencyCode]);

  useEffect(() => {
    if (referral?.patient_postcode) setPostcode(referral.patient_postcode);
  }, [referral?.patient_postcode, setPostcode]);

  useEffect(() => {
    if (referral?.ref_reason_code) setReasonCode(referral.ref_reason_code);
  }, [referral?.ref_reason_code, setReasonCode]);

  useEffect(() => {
    if (referral?.ref_case_reason && !referral.ref_reason_code) {
      setOtherReason(referral.ref_case_reason);
      setReasonCode(OTHER_OPTION.value);
    }
  }, [referral?.ref_case_reason, referral?.ref_reason_code, setOtherReason, setReasonCode]);

  useEffect(() => {
    if (referral?.ref_clinician_user_guid)
      setReferredClinician({
        cat_code: referral.ref_case_spec_cat_code,
        cat_name: referral.ref_case_spec_cat_desc,
        clin_user_guid: referral.ref_clinician_user_guid,
        first_name: referral.ref_clinician || undefined,
        hospital_id: referral.hospital_id || undefined,
        hospital_name: referral.hospital_name || undefined,
        spec_areas: referral.ref_case_spec_area_desc
      });
  }, [
    referral?.hospital_id,
    referral?.hospital_name,
    referral?.ref_case_spec_area_desc,
    referral?.ref_case_spec_cat_code,
    referral?.ref_case_spec_cat_desc,
    referral?.ref_clinician,
    referral?.ref_clinician_user_guid,
    setReferredClinician
  ]);

  useEffect(() => {
    if (comment) setActionTaskComment(comment || undefined);
  }, [comment, setActionTaskComment]);

  const [previousSubType, setPreviousSubType] = useState<string | undefined>();

  // Deselect the speciality and subspeciality if the subtype changes to one that doesn't support the selected speciality
  useEffect(() => {
    if (
      !previousSubType ||
      selectedSubTypeMeta?.action_spec_cat_codes?.find((v) => v === speciality) ||
      previousSubType === selectedSubType
    ) {
      setPreviousSubType(selectedSubType);
      return;
    }

    setPreviousSubType(selectedSubType);
    setSpeciality(undefined);
    setSubspeciality(undefined);
  }, [
    previousSubType,
    selectedSubType,
    selectedSubTypeMeta?.action_spec_cat_codes,
    setSpeciality,
    setSubspeciality,
    speciality
  ]);
};

export const FaceToFaceFieldset = () => {
  const {
    selectedSubType,
    SUBTYPE_KEY,
    AUTH_KEY,
    AUTH_START_KEY,
    SPECIALITY_KEY,
    SUBSPECIALITY_KEY,
    REASON_KEY,
    OTHER_REASON_KEY,
    URGENCY_KEY,
    REFCLI_KEY,
    COMMENT_KEY
  } = useSessionAction(ACTION_REFF2F_CODE);

  const { t } = useTranslation('common');

  const { appointment } = useSessionAppointment();
  const appt_id = useSessionAppointmentId();
  const { detailsCurrentAppointment } = useAppointmentDetails(appt_id);
  const { maybeShowDPACheckConfirmationModal } = useDPACheck();

  const savedAction = detailsCurrentAppointment?.appt_actions?.find(
    ({ appt_action_code }) => appt_action_code === ACTION_REFF2F_CODE
  );

  const [subType, setSubType] = useSessionFormState(SUBTYPE_KEY, savedAction?.action_subtype_code || undefined);
  const [speciality] = useSessionFormState(SPECIALITY_KEY);
  const [subspeciality] = useSessionFormState(SUBSPECIALITY_KEY);
  const [ref_reason_code, setReasonCode] = useSessionFormState(REASON_KEY);
  const [otherReason, setOtherReason] = useSessionFormState(OTHER_REASON_KEY);
  const [urg_code] = useSessionFormState(URGENCY_KEY);
  const [referredClinician] = useSessionFormState(REFCLI_KEY);
  const [auth, setAuth] = useSessionFormState(AUTH_KEY, !!savedAction?.auth_granted);
  const [, setActionTaskComment] = useSessionFormState(COMMENT_KEY);
  const [reasonCode] = useSessionFormState(REASON_KEY);

  const isReasonSet = reasonCode && (reasonCode !== OTHER_OPTION.value || !!otherReason);

  const [authStartDate, setAuthStartDate] = useSessionFormState(
    AUTH_START_KEY,
    savedAction?.auth_start_date ? new Date(savedAction.auth_start_date) : undefined
  );

  const [mounted, setMounted] = useState(false);

  const { mutate } = useFaceToFaceReferralMutation();
  const debouncedMutate = useDebounceCallback(mutate, 500);

  useInitialiseFaceToFaceFieldset();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mounted) debouncedMutate();
  }, [
    referredClinician,
    speciality,
    subspeciality,
    urg_code,
    otherReason,
    ref_reason_code,
    selectedSubType,
    debouncedMutate
  ]);

  useEffect(() => {
    if (appointment.appointment_completed || appointment.read_only || !authStartDate) {
      return;
    }

    const shouldClearAuthDates = isDateBeforeTomorrow(dayjs(authStartDate).toDate());
    if (shouldClearAuthDates) {
      alertModal({
        title: t('Invalid Self-Booking Date'),
        content: t('Selected dates are in the past. Please choose dates in the future.')
      });
      setAuthStartDate(undefined);
      debouncedMutate();
    }
  }, [authStartDate, appointment.appointment_completed, appointment.read_only]);

  const inputRequirements = useFaceToFaceFieldsetRequirements();
  // no selected subtype returns undefined
  const hideAdditionalInfoFormItem = ['PARTPROV', 'PRIVCLINIC', undefined];
  const showDetailsOfClinicFormItem = ['PRIVCLINIC'];

  const onSelectActionSubtype = (newSubTypeValue: string) => {
    if (newSubTypeValue === subType) return;

    setSubType(newSubTypeValue);
    setReasonCode(undefined);
    setActionTaskComment(undefined);
    setOtherReason(undefined);
    setAuth(undefined);
    setAuthStartDate(undefined);

    if (newSubTypeValue === 'CHK4CANC') {
      maybeShowDPACheckConfirmationModal({
        action: 'make a USC - Urgent Suspected Cancer referral'
      });
    }

    debouncedMutate();
  };

  return (
    <>
      <ActionSubtypeFormItem actionCode={ACTION_REFF2F_CODE} onChange={onSelectActionSubtype} />

      {inputRequirements[AUTH_KEY] !== 'disabled' && (
        <AuthSelfBookFormItem
          actionCode={ACTION_REFF2F_CODE}
          onChange={() => {
            debouncedMutate();
          }}
        />
      )}

      {auth && inputRequirements[AUTH_START_KEY] !== 'disabled' && (
        <div>
          <AuthStartDateFormItem
            actionCode={ACTION_REFF2F_CODE}
            onChange={() => {
              debouncedMutate();
            }}
          />
          <Alert
            message={<>{t('The patient will be sent an email and allowed to book their own appointment.')}</>}
            type="info"
            showIcon
            style={{ margin: '1.5rem 0' }}
          />
        </div>
      )}
      {inputRequirements[REASON_KEY] !== 'disabled' && <ReasonForReferralFormItem actionCode={ACTION_REFF2F_CODE} />}

      {inputRequirements[SPECIALITY_KEY] !== 'disabled' && (
        <SpecialityFormItem actionCode={ACTION_REFF2F_CODE} disabled={!isReasonSet} />
      )}
      {inputRequirements[SUBSPECIALITY_KEY] !== 'disabled' && (
        <SubspecialityFormItem actionCode={ACTION_REFF2F_CODE} disabled={!isReasonSet} />
      )}

      {inputRequirements[REFCLI_KEY] !== 'disabled' && !auth && <SelectClinicianFormItem disabled={!isReasonSet} />}
      {inputRequirements[URGENCY_KEY] !== 'disabled' && <FaceToFaceUrgencyFormItem disabled={!isReasonSet} />}

      {!auth && !hideAdditionalInfoFormItem.includes(selectedSubType) && (
        <AdditionalInformationFormItem actionCode={ACTION_REFF2F_CODE} />
      )}
      {showDetailsOfClinicFormItem.includes(selectedSubType || '') && (
        <DetailsOfClinicFormItem actionCode={ACTION_REFF2F_CODE} />
      )}
      {!auth && <Alert message={t('A task will be created and sent back to the referrer.')} type="info" showIcon />}
    </>
  );
};
